import { type RefObject, useEffect } from "react"

/**
 * `useFirstErrorFocus` — after an Inertia validation re-render, move focus
 * + scroll-into-view onto the first invalid field in DOM order.
 *
 * Why: Inertia preserves the mount across re-renders, so `autoFocus` only
 * fires on initial mount and keyboard / SR users lose their place after
 * a failed submit. This hook re-fires every time the `errors` prop
 * identity changes (Inertia hands a new object per re-render) and
 * focuses the matched field's focusable host.
 *
 * Resolution precedence (see {@link findFirstErrorTarget}):
 *   1. `[data-error-anchor="${key}"]` synthetic anchor anywhere inside
 *      the form. Authors opt in to a focus target when the `name`
 *      attribute doesn't sit on a focusable element (Radix `RadioGroup`
 *      `BubbleInput`, Radix `Select` portaled listbox, etc.).
 *   2. `[name]` walk in DOM order. Names match the error key as
 *      `name === key`, `name === "mission[${key}]"`, or
 *      `name.endsWith("[${key}]")`.
 *      - Focusable match (non-hidden input, textarea, select,
 *        `[contenteditable="true"]`, `[tabindex]:not([tabindex="-1"])`)
 *        → focus directly.
 *      - Non-focusable match (hidden input, `tabindex="-1"`) → climb
 *        `closest("[data-rt-editor-root]")` and focus the first
 *        focusable descendant inside it. The contract that
 *        `RichTextEditor`'s hidden-input wire shape uses to expose its
 *        TipTap contenteditable as the focus target for
 *        `mission[brief]`.
 *   3. Multiple keys with multiple candidates → the candidate whose
 *      resolved focusable element is earliest in document order wins.
 *
 * Accessibility:
 *   - `.focus({ preventScroll: true })` then `.scrollIntoView({ block:
 *     "center", behavior })` — the explicit scroll wins; the browser's
 *     default focus-scroll doesn't race.
 *   - `behavior` is `"smooth"` unless `prefers-reduced-motion: reduce`
 *     is set, in which case `"auto"` (instant).
 *   - SSR-safe: returns early when `formRef.current` is null;
 *     `window.matchMedia` is only read inside the effect.
 *
 * @example
 * ```tsx
 * function MissionFormBody({ errors }: { errors: Record<string, unknown> }) {
 *   const formRef = useRef<HTMLDivElement>(null)
 *   useFirstErrorFocus(errors, formRef)
 *   return <div ref={formRef}>{ ... }</div>
 * }
 * ```
 */
export function useFirstErrorFocus(
  errors: Record<string, unknown>,
  formRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const root = formRef.current
    if (root === null) return
    if (Object.keys(errors).length === 0) return

    const target = findFirstErrorTarget(errors, root)
    if (target === null) return

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    target.focus({ preventScroll: true })
    target.scrollIntoView({
      block: "center",
      behavior: prefersReducedMotion ? "auto" : "smooth",
    })
  }, [errors, formRef])
}

/**
 * Pure-DOM resolver: given an `errors` map and a `root` element,
 * return the focusable element that should receive focus first, or
 * `null` if nothing matches.
 *
 * Exported separately so it can be unit-tested without mounting React
 * — see `__tests__/find-first-error-target.test.ts`.
 *
 * See {@link useFirstErrorFocus} for precedence rules.
 */
export function findFirstErrorTarget(
  errors: Record<string, unknown>,
  root: HTMLElement,
): HTMLElement | null {
  const keys = Object.keys(errors)
  if (keys.length === 0) return null

  const candidates: HTMLElement[] = []

  for (const key of keys) {
    const resolved = resolveTargetForKey(root, key)
    if (resolved !== null) candidates.push(resolved)
  }

  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]

  return earliestInDocumentOrder(candidates)
}

function resolveTargetForKey(
  root: HTMLElement,
  key: string,
): HTMLElement | null {
  // Precedence 1: explicit synthetic anchor.
  const anchor = root.querySelector<HTMLElement>(
    `[data-error-anchor="${cssEscape(key)}"]`,
  )
  if (anchor !== null) return anchor

  // Precedence 2: walk `[name]` matches in DOM order, pick the first
  // whose name matches the key (bare / bracketed / suffix).
  const named = root.querySelectorAll<HTMLElement>("[name]")
  for (const node of named) {
    if (!nameMatchesKey(node.getAttribute("name"), key)) continue

    if (isFocusable(node)) return node

    // Non-focusable match (hidden input, tabindex="-1"). Climb to a
    // RichTextEditor host and grab the contenteditable / focusable
    // descendant.
    const host = node.closest<HTMLElement>("[data-rt-editor-root]")
    if (host === null) continue
    const focusableInHost = host.querySelector<HTMLElement>(
      '[contenteditable="true"], [tabindex]:not([tabindex="-1"])',
    )
    if (focusableInHost !== null) return focusableInHost
  }

  return null
}

function nameMatchesKey(name: string | null, key: string): boolean {
  if (name === null) return false
  if (name === key) return true
  // Bracketed Rails wrapper: e.g. `mission[title]` matches key `title`.
  return name.endsWith(`[${key}]`)
}

function isFocusable(node: HTMLElement): boolean {
  const tabindexAttr = node.getAttribute("tabindex")
  if (tabindexAttr === "-1") return false

  if (
    node instanceof HTMLInputElement &&
    node.type.toLowerCase() === "hidden"
  ) {
    return false
  }

  if (
    node instanceof HTMLInputElement ||
    node instanceof HTMLTextAreaElement ||
    node instanceof HTMLSelectElement
  ) {
    return true
  }

  if (node.isContentEditable) return true
  if (tabindexAttr !== null) return true

  return false
}

function earliestInDocumentOrder(nodes: HTMLElement[]): HTMLElement {
  let earliest = nodes[0]
  for (let i = 1; i < nodes.length; i++) {
    const candidate = nodes[i]
    // Node.DOCUMENT_POSITION_FOLLOWING (4) on `earliest.compareDocumentPosition(candidate)`
    // means `candidate` follows `earliest` in document order → `earliest` is earlier; keep it.
    // Otherwise `candidate` is earlier → swap.
    const relation = earliest.compareDocumentPosition(candidate)
    if ((relation & Node.DOCUMENT_POSITION_PRECEDING) !== 0) {
      earliest = candidate
    }
  }
  return earliest
}

/**
 * Minimal CSS attribute-value escape — covers the characters that
 * appear in error keys (`[`, `]`, `\`, `"`). Rails error keys are
 * snake_case identifiers, so the practical risk is low, but keys can
 * arrive bracketed (e.g. `mission[title]`) when the server doesn't
 * normalize; defending the selector is cheap.
 */
function cssEscape(value: string): string {
  if (
    typeof window !== "undefined" &&
    typeof window.CSS?.escape === "function"
  ) {
    return window.CSS.escape(value)
  }
  return value.replace(/(["\\[\]])/g, "\\$1")
}
